import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const FACE_CHECK_INTERVAL = 3000;
const OBJECT_CHECK_INTERVAL = 4000;
const BANNED_OBJECTS = ['cell phone', 'laptop', 'book', 'remote', 'tablet'];

/**
 * useAntiCheat — encapsulates webcam capture + face presence + banned-object
 * detection used by proctored quizzes. Returns refs / state the consuming
 * component renders, plus a setter to indicate the quiz is finished so the
 * hook can clean up intervals.
 *
 * @param {{ maxWarnings: number, submitting: boolean }} opts
 */
export default function useAntiCheat({ maxWarnings = 3, submitting = false } = {}) {
  const [cameraGranted, setCameraGranted] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [faceStatus, setFaceStatus] = useState('detecting'); // 'ok' | 'no_face' | 'multiple' | 'detecting'
  const [warnings, setWarnings] = useState(0);
  const [deviceDetected, setDeviceDetected] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const objectDetectorRef = useRef(null);
  const faceCheckRef = useRef(null);
  const objectCheckRef = useRef(null);

  const requestCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 320 }, height: { ideal: 240 } },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraGranted(true);
      setCameraError('');
    } catch (err) {
      setCameraError('Camera access is required to take the quiz. Please allow camera permission and try again.');
      setCameraGranted(false);
    }
  }, []);

  const initDetectors = useCallback(async () => {
    try {
      const tf = await import('@tensorflow/tfjs');
      await tf.setBackend('webgl');
      await tf.ready();

      try {
        const blazeface = await import('@tensorflow-models/blazeface');
        detectorRef.current = await blazeface.load();
      } catch (err) {
        console.error('BlazeFace load failed:', err);
      }

      try {
        const cocoSsd = await import('@tensorflow-models/coco-ssd');
        objectDetectorRef.current = await cocoSsd.load({ base: 'lite_mobilenet_v2' });
      } catch (err) {
        console.error('COCO-SSD load failed:', err);
      }

      setFaceStatus('ok');
    } catch (err) {
      console.error('TF.js init failed:', err);
      setFaceStatus('ok');
    }
  }, []);

  // Periodic face presence check
  useEffect(() => {
    if (!cameraGranted || submitting) return;
    const checkFace = async () => {
      if (!videoRef.current || !detectorRef.current) return;
      const video = videoRef.current;
      if (video.readyState < 2 || video.videoWidth === 0) return;
      try {
        const predictions = await detectorRef.current.estimateFaces(video, false);
        if (predictions.length === 0) {
          setFaceStatus('no_face');
          setWarnings((prev) => {
            const next = prev + 1;
            if (next === 1) toast.warning('No face detected! Please look at the screen.');
            else if (next === 2) toast.warning('Warning 2/3: Face not visible. Quiz will auto-submit after 3 warnings.');
            else if (next >= maxWarnings) toast.error('Too many face violations. Quiz auto-submitting...');
            return next;
          });
        } else if (predictions.length > 1) {
          setFaceStatus('multiple');
          setWarnings((prev) => {
            const next = prev + 1;
            if (next === 1) toast.warning('Multiple faces detected! Only the quiz taker should be visible.');
            else if (next >= maxWarnings) toast.error('Too many violations. Quiz auto-submitting...');
            return next;
          });
        } else {
          setFaceStatus('ok');
        }
      } catch (err) {
        // transient TF errors are ok
      }
    };
    faceCheckRef.current = setInterval(checkFace, FACE_CHECK_INTERVAL);
    return () => { if (faceCheckRef.current) clearInterval(faceCheckRef.current); };
  }, [cameraGranted, submitting, maxWarnings]);

  // Periodic banned-object detection
  useEffect(() => {
    if (!cameraGranted || submitting) return;
    const checkObjects = async () => {
      if (!videoRef.current || !objectDetectorRef.current) return;
      const video = videoRef.current;
      if (video.readyState < 2 || video.videoWidth === 0) return;
      try {
        const predictions = await objectDetectorRef.current.detect(video);
        const banned = predictions.find((p) => BANNED_OBJECTS.includes(p.class) && p.score > 0.5);
        if (banned) {
          const deviceName = banned.class === 'cell phone' ? 'Phone' : banned.class.charAt(0).toUpperCase() + banned.class.slice(1);
          setDeviceDetected(deviceName);
          setWarnings((prev) => {
            const next = prev + 1;
            if (next <= maxWarnings) toast.error(`${deviceName} detected! Remove all electronic devices and books.`);
            if (next >= maxWarnings) toast.error('Too many violations. Quiz auto-submitting...');
            return next;
          });
        } else {
          setDeviceDetected(null);
        }
      } catch (err) {
        // silent
      }
    };
    objectCheckRef.current = setInterval(checkObjects, OBJECT_CHECK_INTERVAL);
    return () => { if (objectCheckRef.current) clearInterval(objectCheckRef.current); };
  }, [cameraGranted, submitting, maxWarnings]);

  // Bootstrap: request camera, then init detectors once granted
  useEffect(() => { requestCamera(); }, [requestCamera]);
  useEffect(() => { if (cameraGranted) initDetectors(); }, [cameraGranted, initDetectors]);

  // Re-attach stream if video element re-mounted
  useEffect(() => {
    if (cameraGranted && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (faceCheckRef.current) clearInterval(faceCheckRef.current);
      if (objectCheckRef.current) clearInterval(objectCheckRef.current);
      detectorRef.current = null;
      objectDetectorRef.current = null;
    };
  }, []);

  return {
    videoRef,
    cameraGranted,
    cameraError,
    requestCamera,
    faceStatus,
    warnings,
    deviceDetected,
    isOverLimit: warnings >= maxWarnings,
  };
}
